import React from 'react';

// Local UI primitives (Button, Card, Select, TextView) — Tailwind-styled, no external UI package.

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost';
  size?: 'sm' | 'md';
}

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  className = '',
  children,
  ...rest
}) => {
  const base =
    'inline-flex items-center justify-center rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
  const sizeCls = size === 'sm' ? 'px-3 py-1.5 text-sm' : 'px-4 py-2 text-sm';
  const variantCls =
    variant === 'secondary'
      ? 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 focus:ring-primary-500'
      : variant === 'ghost'
      ? 'bg-transparent text-primary-600 hover:bg-primary-50 focus:ring-primary-500'
      : 'bg-primary-600 text-white hover:bg-primary-700 focus:ring-primary-500';

  return (
    <button className={`${base} ${sizeCls} ${variantCls} ${className}`} {...rest}>
      {children}
    </button>
  );
};

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  elevation?: number;
}

export const Card: React.FC<CardProps> = ({ className = '', children, ...rest }) => {
  return (
    <div
      className={`card ${className}`}
      {...rest}
    >
      {children}
    </div>
  );
};

export interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  options?: Array<{
    value: string;
    label: string;
    disabled?: boolean;
  }>;
  placeholder?: string;
  fullWidth?: boolean;
  onValueChange?: (value: string) => void;
}

export const Select: React.FC<SelectProps> = ({
  className = '',
  children,
  options,
  placeholder,
  fullWidth,
  onValueChange,
  onChange,
  ...rest
}) => {
  return (
    <select
      className={`select-field text-sm ${fullWidth === false ? '' : 'w-full'} ${className}`}
      onChange={(e) => {
        onChange?.(e);
        onValueChange?.(e.target.value);
      }}
      {...rest}
    >
      {placeholder ? <option value="">{placeholder}</option> : null}
      {options?.map((option) => (
        <option key={option.value} value={option.value} disabled={option.disabled}>
          {option.label}
        </option>
      ))}
      {children}
    </select>
  );
};

export interface TextViewProps extends React.HTMLAttributes<HTMLElement> {
  variant?:
    | 'heading-1'
    | 'heading-2'
    | 'heading-3'
    | 'body'
    | 'body-sm'
    | 'label-xs'
    | string;
}

export const TextView: React.FC<TextViewProps> = ({ variant = 'body', className = '', children, ...rest }) => {
  let Tag: keyof JSX.IntrinsicElements = 'p';
  let base = 'text-sm text-gray-700';

  if (variant === 'heading-1') {
    Tag = 'h1';
    base = 'text-2xl font-bold text-gray-900';
  } else if (variant === 'heading-2') {
    Tag = 'h2';
    base = 'text-xl font-semibold text-gray-900';
  } else if (variant === 'heading-3') {
    Tag = 'h3';
    base = 'text-base font-semibold text-gray-900';
  } else if (variant === 'body-sm') {
    base = 'text-xs text-gray-500';
  } else if (variant === 'label-xs') {
    base = 'text-xs font-medium text-gray-500 uppercase tracking-wide';
  }

  return (
    <Tag className={`${base} ${className}`} {...rest}>
      {children}
    </Tag>
  );
};

export interface DatePickerProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
}

export const DatePicker: React.FC<DatePickerProps> = ({ className = '', ...rest }) => {
  return (
    <input
      type="date"
      className={`input-field text-sm ${className}`}
      {...rest}
    />
  );
};


